import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { LogOut, Trash2, Users, TrendingUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

export default function AdminDashboard() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [wl, st] = await Promise.all([
        ax.get(`${API}/admin/waitlist`),
        ax.get(`${API}/admin/stats`),
      ]);
      setEntries(wl.data.entries);
      setStats(st.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      await ax.delete(`${API}/admin/waitlist/${id}`);
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    }
  };

  const handleLogout = async () => {
    await ax.post(`${API}/auth/logout`);
    navigate("/admin");
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--blush)", fontFamily: "var(--font-sans)", color: "var(--charcoal-light)" }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen"
      style={{ backgroundColor: "var(--blush)" }}
    >
      {/* Header */}
      <header
        className="flex items-center justify-between px-6 py-4"
        style={{
          backgroundColor: "var(--white)",
          borderBottom: "1px solid rgba(44,44,42,0.06)",
        }}
      >
        <div className="flex items-center gap-3">
          <svg width="24" height="24" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <circle cx="16" cy="16" r="15" stroke="#D85A30" strokeWidth="1.2" opacity="0.3" />
            <circle cx="16" cy="16" r="10" stroke="#D85A30" strokeWidth="1.2" opacity="0.5" />
            <circle cx="16" cy="16" r="5" stroke="#D85A30" strokeWidth="1.5" opacity="0.8" />
            <circle cx="16" cy="16" r="1.5" fill="#D85A30" />
          </svg>
          <span
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "1.15rem",
              fontWeight: 600,
              color: "var(--charcoal)",
            }}
          >
            Depth Co. Admin
          </span>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-2"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.85rem",
            color: "var(--charcoal-light)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px 12px",
            borderRadius: "8px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(44,44,42,0.04)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          <LogOut size={16} /> Sign out
        </button>
      </header>

      {/* Stats */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div
            data-testid="admin-stat-total"
            style={{
              backgroundColor: "var(--white)",
              borderRadius: "14px",
              padding: "1.25rem 1.5rem",
              boxShadow: "0 2px 12px rgba(44,44,42,0.04)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} color="var(--coral)" />
              <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", color: "var(--charcoal-light)", fontWeight: 500 }}>
                Total signups
              </span>
            </div>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: "2rem", fontWeight: 600, color: "var(--charcoal)" }}>
              {stats.total}
            </span>
          </div>
          <div
            data-testid="admin-stat-today"
            style={{
              backgroundColor: "var(--white)",
              borderRadius: "14px",
              padding: "1.25rem 1.5rem",
              boxShadow: "0 2px 12px rgba(44,44,42,0.04)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} color="var(--coral)" />
              <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", color: "var(--charcoal-light)", fontWeight: 500 }}>
                Today
              </span>
            </div>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: "2rem", fontWeight: 600, color: "var(--charcoal)" }}>
              {stats.today}
            </span>
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            backgroundColor: "var(--white)",
            borderRadius: "14px",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(44,44,42,0.04)",
          }}
        >
          <div
            className="px-5 py-4"
            style={{
              borderBottom: "1px solid rgba(44,44,42,0.06)",
              fontFamily: "var(--font-serif)",
              fontSize: "1.15rem",
              fontWeight: 600,
              color: "var(--charcoal)",
            }}
          >
            Waitlist Entries
          </div>
          {entries.length === 0 ? (
            <div
              className="py-12 text-center"
              style={{ fontFamily: "var(--font-sans)", color: "var(--charcoal-light)", fontSize: "0.9rem" }}
            >
              No signups yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", fontWeight: 600 }}>Email</TableHead>
                  <TableHead style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", fontWeight: 600 }}>Source</TableHead>
                  <TableHead style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", fontWeight: 600 }}>Date</TableHead>
                  <TableHead style={{ fontFamily: "var(--font-sans)", fontSize: "0.75rem", fontWeight: 600, width: "50px" }}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`waitlist-entry-${entry.email}`}>
                    <TableCell style={{ fontFamily: "var(--font-sans)", fontSize: "0.875rem" }}>{entry.email}</TableCell>
                    <TableCell style={{ fontFamily: "var(--font-sans)", fontSize: "0.8rem", color: "var(--charcoal-light)" }}>{entry.source}</TableCell>
                    <TableCell style={{ fontFamily: "var(--font-sans)", fontSize: "0.8rem", color: "var(--charcoal-light)" }}>
                      {new Date(entry.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid={`delete-entry-${entry.email}`}
                        onClick={() => handleDelete(entry.id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--charcoal-light)",
                          padding: "4px",
                          borderRadius: "6px",
                          transition: "color 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#c53030")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--charcoal-light)")}
                        aria-label="Delete entry"
                      >
                        <Trash2 size={15} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
